import { DataAccess } from "../../dataaccess/data.access";
import { FileStoreUtil } from "../../util/filestore.util";
import { S25Util } from "../../util/s25-util";

export type LogoObjectRef = {
    objectType: number;
    objectId: number;
};

type LogoUrlsResponse = {
    urls?: {
        objectType: number;
        objectId: number;
        url: string;
    }[];
};

const logoCache = new Map<string, string | undefined>();
const pendingByKey = new Map<string, Promise<string | undefined>>();

function logoCacheKey(objectType: number, objectId: number): string {
    return `${objectType}-${objectId}`;
}

export function transformLogoDisplayUrl(url: string | undefined): string | undefined {
    if (!url) {
        return undefined;
    }
    if (url.includes("://")) {
        return S25Util.replace25LiveUrl(url);
    }
    if (FileStoreUtil.isFileStoreUrl(url)) {
        return DataAccess.getUrl(url);
    }
    if (url.startsWith("/")) {
        return DataAccess.getUrl(url);
    }
    return undefined;
}

async function fetchLogoUrlsBulk(refs: LogoObjectRef[]): Promise<Map<string, string | undefined>> {
    const seenKeys = new Set<string>();
    const uniqueRefs = refs.filter((ref) => {
        const cacheKey = logoCacheKey(ref.objectType, ref.objectId);
        if (seenKeys.has(cacheKey)) {
            return false;
        }
        seenKeys.add(cacheKey);
        return true;
    });
    if (!uniqueRefs.length) {
        return new Map();
    }

    const response = await DataAccess.post<LogoUrlsResponse>(
        DataAccess.injectCaller("/se/object/logos.json", "LogoUrlCoordinator.fetchLogoUrlsBulk"),
        { objects: uniqueRefs },
    );

    const resolved = new Map<string, string | undefined>();
    for (const entry of response?.urls ?? []) {
        const cacheKey = logoCacheKey(entry.objectType, entry.objectId);
        const displayUrl = transformLogoDisplayUrl(entry.url);
        logoCache.set(cacheKey, displayUrl);
        resolved.set(cacheKey, displayUrl);
    }

    for (const ref of uniqueRefs) {
        const cacheKey = logoCacheKey(ref.objectType, ref.objectId);
        if (!resolved.has(cacheKey)) {
            logoCache.set(cacheKey, undefined);
            resolved.set(cacheKey, undefined);
        }
    }

    return resolved;
}

const debouncedBulkFetch = S25Util.debounceTs({
    fn: (refs: LogoObjectRef[]) => fetchLogoUrlsBulk(refs),
    ms: 50,
    rejectOnClear: false,
    accumulateArgNum: 0,
    resolveOnFinal: true,
}) as (refs: LogoObjectRef[]) => Promise<Map<string, string | undefined>>;

export async function getLogoUrlCoordinated(objectType: number, objectId: number): Promise<string | undefined> {
    const cacheKey = logoCacheKey(objectType, objectId);
    if (logoCache.has(cacheKey)) {
        return logoCache.get(cacheKey);
    }

    let pending = pendingByKey.get(cacheKey);
    if (!pending) {
        pending = debouncedBulkFetch([{ objectType, objectId }]).then((resolved) => resolved.get(cacheKey));
        pendingByKey.set(cacheKey, pending);
        pending.finally(() => pendingByKey.delete(cacheKey));
    }
    return pending;
}

export async function getLogoUrlsCoordinated(refs: LogoObjectRef[]): Promise<Map<string, string | undefined>> {
    const uncachedRefs = refs.filter((ref) => !logoCache.has(logoCacheKey(ref.objectType, ref.objectId)));
    if (uncachedRefs.length) {
        await fetchLogoUrlsBulk(uncachedRefs);
    }

    const result = new Map<string, string | undefined>();
    for (const ref of refs) {
        const cacheKey = logoCacheKey(ref.objectType, ref.objectId);
        result.set(cacheKey, logoCache.get(cacheKey));
    }
    return result;
}
